import { Count, Popover } from '@bifeai/ui';
import { ClipboardList, MessagesSquare, ShoppingBag, Volume2, VolumeX } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import type { OrdersChatSummaryDTO } from '@bifeai/shared';

const MUTE_KEY = 'bifeai:pedidos-sin-sonido';

function readMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Dos tonos cortos, sin archivos de sonido. */
function ding(): void {
  try {
    const ctx = new AudioContext();
    [880, 1320].forEach((freq, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = freq;
      o.connect(g);
      g.connect(ctx.destination);
      const t = ctx.currentTime + i * 0.18;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.25, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
      o.start(t);
      o.stop(t + 0.17);
    });
    setTimeout(() => void ctx.close(), 600);
  } catch {
    // Sin audio (navegador viejo o bloqueado): queda solo el contador.
  }
}

/**
 * Campanita de pedidos: separada de las alertas. Cuenta pedidos nuevos y conversaciones que esperan a una persona,
 * y suena cuando entra un pedido (el sonido se puede apagar en cada computadora).
 */
export function OrdersBell({ summary }: { summary: OrdersChatSummaryDTO | undefined }) {
  const [open, setOpen] = useState(false);
  const [muted, setMuted] = useState(readMuted);
  const last = useRef<string | null | undefined>(undefined);
  const latest = summary?.latestOrderId;
  useEffect(() => {
    if (latest === undefined) return;
    // La primera lectura no suena: solo los pedidos que entran mientras la pantalla está abierta.
    if (last.current !== undefined && latest && latest !== last.current && !muted) ding();
    last.current = latest;
  }, [latest, muted]);
  const toggleMute = () => {
    const v = !muted;
    setMuted(v);
    try {
      localStorage.setItem(MUTE_KEY, v ? '1' : '0');
    } catch {
      // Sin almacenamiento: vale para esta pantalla.
    }
  };
  const total = (summary?.newOrders ?? 0) + (summary?.waitingHuman ?? 0);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger={
        <button type="button" className="bf-icon-btn" aria-label={total ? `Pedidos: ${total} para atender` : 'Pedidos'}>
          <ShoppingBag />
          <Count value={total} tone="info" />
        </button>
      }
    >
      <div className="bf-notif-head">
        <strong style={{ fontFamily: 'var(--bf-font-display)' }}>Pedidos y chat</strong>
        <button
          type="button"
          className="bf-icon-btn"
          onClick={toggleMute}
          aria-label={muted ? 'Activar sonido de pedidos' : 'Silenciar sonido de pedidos'}
        >
          {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>
      </div>
      <div style={{ display: 'grid', gap: 4, padding: '4px 0' }}>
        <Link
          to="/pedidos"
          className="bf-menu-link"
          onClick={() => setOpen(false)}
          style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px' }}
        >
          <ClipboardList size={16} aria-hidden />
          <span style={{ flex: 1 }}>Pedidos nuevos</span>
          <strong>{summary?.newOrders ?? 0}</strong>
        </Link>
        <Link
          to="/pedidos"
          className="bf-menu-link"
          onClick={() => setOpen(false)}
          style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px' }}
        >
          <ClipboardList size={16} aria-hidden />
          <span style={{ flex: 1 }}>Listos para entregar</span>
          <strong>{summary?.readyOrders ?? 0}</strong>
        </Link>
        <Link
          to="/chat"
          className="bf-menu-link"
          onClick={() => setOpen(false)}
          style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px' }}
        >
          <MessagesSquare size={16} aria-hidden />
          <span style={{ flex: 1 }}>Esperan a una persona</span>
          <strong>{summary?.waitingHuman ?? 0}</strong>
        </Link>
        <Link
          to="/chat"
          className="bf-menu-link"
          onClick={() => setOpen(false)}
          style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px' }}
        >
          <MessagesSquare size={16} aria-hidden />
          <span style={{ flex: 1 }}>Conversaciones sin leer</span>
          <strong>{summary?.unreadConversations ?? 0}</strong>
        </Link>
      </div>
    </Popover>
  );
}
